(() => {
  'use strict';
  const failure = message => Object.assign(new Error(message), { code: 'CART_INTENT_CHANGED' });
  const changedMessage = 'Your cart changed. Review the latest quantities and try again.';

  /** UI intentions only. The existing controller still owns fresh stock,
   * prices, delivery planning, journals, mutations and uncertain-write recovery.
   * read() returns {cart, contextKey, blocked}; commit() must return its confirmed
   * {cart} result, or reject. A resolved undefined result is not confirmation. */
  const createQuantityQueue = ({ read, commit, stateOf, notify = () => {}, onError = () => {},
    delayMs = 180, maximumQuantity = 99, setTimer = setTimeout, clearTimer = clearTimeout }) => {
    if (typeof read !== 'function' || typeof commit !== 'function' || typeof stateOf !== 'function'
      || !Number.isSafeInteger(delayMs) || delayMs < 150 || delayMs > 250
      || !Number.isSafeInteger(maximumQuantity) || maximumQuantity < 1) throw new TypeError('Invalid quantity queue configuration.');
    const entries = new Map(), waiters = [];
    let timer = null, running = false, active = null, sequence = 0, disposed = false, lastError = null;
    const lineState = (cart, line) => stateOf({ currency: cart.currency, items: [line] });
    const identity = (cart, line) => lineState(cart, { ...line, quantity: 1 });
    const report = error => { try { onError(error); } catch { /* Reporting cannot replay a cart mutation. */ } };
    const pending = () => entries.size > 0 || running;
    const current = () => {
      const value = read();
      if (disposed || value?.blocked || !value?.contextKey || !value.cart?.token
        || !Array.isArray(value.cart.items) || !/^[A-Z]{3}$/.test(value.cart.currency)) throw failure(changedMessage);
      return value;
    };
    const resolve = (entry, view) => {
      if (view.contextKey !== entry.contextKey || view.cart.token !== entry.cartToken) throw failure(changedMessage);
      const matches = view.cart.items.filter(line => identity(view.cart, line) === entry.identity);
      if (matches.length !== 1 || typeof matches[0].key !== 'string' || !matches[0].key
        || lineState(view.cart, matches[0]) !== entry.baseState) throw failure(changedMessage);
      return matches[0];
    };
    const snapshot = () => ({
      pending: pending(), isSubmitting: running, error: lastError,
      items: [...entries.values()].map(entry => ({ identity: entry.identity, key: entry.key,
        quantity: entry.desiredQuantity, confirmedQuantity: entry.baseQuantity,
        submitting: active?.identity === entry.identity,
        removing: entry.desiredQuantity === 0,
        removalSubmitted: active?.identity === entry.identity && active.quantity === 0 })),
    });
    const emit = () => { try { notify(snapshot()); } catch (error) { report(error); } };
    const settle = () => { if (!pending()) waiters.splice(0).forEach(resolveWait => resolveWait(snapshot())); };
    const stopTimer = () => { if (timer !== null) { clearTimer(timer); timer = null; } };
    const clearQueued = error => {
      stopTimer(); entries.clear(); lastError = error || null;
      if (error) report(error);
      emit(); settle();
    };
    const process = async () => {
      if (running || disposed) return;
      running = true; emit();
      try {
        while (entries.size && !disposed) {
          const entry = [...entries.values()].sort((a, b) => a.sequence - b.sequence)[0];
          const view = current(), line = resolve(entry, view);
          if (entry.desiredQuantity === entry.baseQuantity) { entries.delete(entry.identity); continue; }
          const quantity = entry.desiredQuantity;
          active = { identity: entry.identity, quantity };
          entry.key = line.key;
          emit();
          const result = await commit({ key: line.key, quantity,
            guard: { quantity: line.quantity, state: entry.baseState },
            identity: entry.identity, contextKey: entry.contextKey });
          if (disposed) break;
          if (!result?.cart || result.cart.token !== entry.cartToken || result.cart.currency !== view.cart.currency
            || !Array.isArray(result.cart.items)) throw failure('Your cart update could not be confirmed. Review your cart before trying again.');
          const matches = result.cart.items.filter(row => identity(result.cart, row) === entry.identity);
          if (quantity === 0) {
            if (matches.length) throw failure('The item removal could not be confirmed. Review your cart before trying again.');
            entries.delete(entry.identity);
          } else {
            if (matches.length !== 1 || matches[0].quantity !== quantity) throw failure(changedMessage);
            const confirmed = matches[0];
            entry.key = confirmed.key; entry.baseQuantity = confirmed.quantity;
            entry.baseState = lineState(result.cart, confirmed);
            if (entry.desiredQuantity === confirmed.quantity) entries.delete(entry.identity);
          }
          active = null;
          // Every queued row must still match its own baseline. Discount-driven
          // key replacement is allowed; changed quantities/properties are not.
          if (entries.size) {
            const latest = current();
            for (const queued of entries.values()) queued.key = resolve(queued, latest).key;
          }
          emit();
          if (timer !== null) break;
        }
      } catch (error) { clearQueued(error instanceof Error ? error : failure(changedMessage)); }
      finally { active = null; running = false; emit(); settle(); }
    };
    const schedule = () => {
      stopTimer();
      if (!entries.size) { emit(); settle(); return; }
      timer = setTimer(() => { timer = null; void process(); }, delayMs);
    };
    const enqueue = (key, delta, remove = false) => {
      try {
        if (typeof key !== 'string' || !key || !remove && ![-1, 1].includes(delta)) return false;
        const view = current(), matches = view.cart.items.filter(line => line.key === key);
        if (matches.length !== 1 || !Number.isSafeInteger(matches[0].quantity) || matches[0].quantity < 1) throw failure(changedMessage);
        const line = matches[0], id = identity(view.cart, line);
        if (view.cart.items.filter(row => identity(view.cart, row) === id).length !== 1) throw failure(changedMessage);
        let entry = entries.get(id);
        if (entry) resolve(entry, view);
        else entry = { identity: id, key, cartToken: view.cart.token, contextKey: view.contextKey,
          baseState: lineState(view.cart, line), baseQuantity: line.quantity, desiredQuantity: line.quantity, sequence: ++sequence };
        // A removal already sent cannot be undone by silently adding an item.
        if (active?.identity === id && active.quantity === 0) return false;
        const target = remove ? 0 : entry.desiredQuantity + delta;
        if (!Number.isSafeInteger(target) || target < 0 || target > maximumQuantity) return false;
        entry.desiredQuantity = target; entries.set(id, entry); lastError = null;
        if (active?.identity !== id && target === entry.baseQuantity) entries.delete(id);
        emit(); schedule(); return true;
      } catch (error) {
        // Do not cancel a submitted write. Its authoritative result still owns
        // recovery; discard only intentions that have not been submitted.
        if (active) {
          const retained = entries.get(active.identity);
          entries.clear();
          if (retained) { retained.desiredQuantity = active.quantity; entries.set(retained.identity, retained); }
          stopTimer(); lastError = error; report(error); emit();
        } else clearQueued(error);
        return false;
      }
    };
    return {
      change: (key, delta) => enqueue(key, delta),
      remove: key => enqueue(key, 0, true),
      snapshot,
      isSubmitting: () => running,
      isPending: pending,
      whenIdle: () => pending() ? new Promise(resolveWait => waiters.push(resolveWait)) : Promise.resolve(snapshot()),
      flush() { stopTimer(); void process(); return this.whenIdle(); },
      cancel() {
        if (running) return false;
        clearQueued(); return true;
      },
      dispose() {
        disposed = true; stopTimer(); entries.clear();
        // A submitted request remains owned by commit; no retry or rollback.
        emit(); settle();
      },
    };
  };
  const exports = { createQuantityQueue };
  if (typeof module !== 'undefined' && module.exports) module.exports = exports;
  if (typeof window !== 'undefined') window.PolygonsNativeCartIntents = exports;
})();
